import { GameObject, Rectangle, Vector2 } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export class Brick implements GameObject {
  public id: string;
  public active: boolean = true;
  public position: Vector2;
  public velocity: Vector2 = { x: 0, y: 0 };
  public size: Vector2;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  
  // Brick properties
  private brickWidth: number;
  private brickHeight: number;
  private color: string;
  private pointValue: number = 10;
  private animationTimer: number = 0;
  private destroyAnimation: number = 0;
  private isDestroying: boolean = false;

  constructor(
    id: string,
    x: number,
    y: number,
    width: number,
    height: number,
    color: string,
    renderManager: RenderManager,
    audioManager: AudioManager
  ) {
    this.id = id;
    this.position = { x, y };
    this.brickWidth = width;
    this.brickHeight = height;
    this.color = color;
    this.size = { x: width, y: height };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
  }

  public update(deltaTime: number): void {
    // Update animation timer
    this.animationTimer += deltaTime;
    
    // Handle destroy animation
    if (this.isDestroying) {
      this.destroyAnimation += deltaTime * 3;
      if (this.destroyAnimation >= 1) {
        this.active = false;
      }
    }
  }

  public setPointValue(value: number): void {
    this.pointValue = value;
  }

  public getPointValue(): number {
    return this.pointValue;
  }

  public destroy(): void {
    if (!this.isDestroying) {
      this.isDestroying = true;
      this.destroyAnimation = 0;
    }
  }

  public isBeingDestroyed(): boolean {
    return this.isDestroying;
  }

  public getPosition(): Vector2 {
    return { ...this.position };
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.brickWidth,
      height: this.brickHeight
    };
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;
    
    if (this.isDestroying) {
      this.renderDestroyAnimation(ctx);
      return;
    }
    
    // Main brick body
    this.renderManager.fillRect(
      this.position.x + 1,
      this.position.y + 1,
      this.brickWidth - 2,
      this.brickHeight - 2,
      this.color
    );
    
    // 3D effect - top highlight
    this.renderManager.fillRect(
      this.position.x + 1,
      this.position.y + 1,
      this.brickWidth - 2,
      2,
      this.lightenColor(this.color, 0.3)
    );
    
    // 3D effect - left highlight
    this.renderManager.fillRect(
      this.position.x + 1,
      this.position.y + 1,
      2,
      this.brickHeight - 2,
      this.lightenColor(this.color, 0.2)
    );
    
    // 3D effect - bottom shadow
    this.renderManager.fillRect(
      this.position.x + 1,
      this.position.y + this.brickHeight - 3,
      this.brickWidth - 2,
      2,
      this.darkenColor(this.color, 0.3)
    );
    
    // 3D effect - right shadow
    this.renderManager.fillRect(
      this.position.x + this.brickWidth - 3,
      this.position.y + 1,
      2,
      this.brickHeight - 2,
      this.darkenColor(this.color, 0.2)
    );
    
    // Border
    this.renderManager.strokeRect(
      this.position.x,
      this.position.y,
      this.brickWidth,
      this.brickHeight,
      '#FFFFFF',
      1
    );
    
    // Subtle shimmer effect
    const shimmer = Math.sin(this.animationTimer * 2 + this.position.x * 0.01) * 0.1 + 0.9;
    if (shimmer > 0.95) {
      this.renderManager.fillRect(
        this.position.x + 3,
        this.position.y + 3,
        Math.max(1, this.brickWidth - 6),
        1,
        `rgba(255, 255, 255, ${(shimmer - 0.95) * 20})`
      );
    }
  }

  private renderDestroyAnimation(ctx: CanvasRenderingContext2D): void {
    const progress = this.destroyAnimation;
    const centerX = this.position.x + this.brickWidth / 2;
    const centerY = this.position.y + this.brickHeight / 2;
    
    // Particle explosion effect
    const numParticles = 8;
    for (let i = 0; i < numParticles; i++) {
      const angle = (i / numParticles) * Math.PI * 2;
      const distance = progress * 30;
      const particleSize = Math.max(1, 4 - progress * 3);
      const alpha = 1 - progress;
      
      const x = centerX + Math.cos(angle) * distance;
      const y = centerY + Math.sin(angle) * distance + progress * progress * 20; // Gravity effect
      
      this.renderManager.fillRect(
        x - particleSize / 2,
        y - particleSize / 2,
        particleSize,
        particleSize,
        `rgba(${this.hexToRgb(this.color).r}, ${this.hexToRgb(this.color).g}, ${this.hexToRgb(this.color).b}, ${alpha})`
      );
    }
    
    // Flash effect
    if (progress < 0.3) {
      const flashAlpha = (0.3 - progress) / 0.3;
      this.renderManager.fillRect(
        this.position.x,
        this.position.y,
        this.brickWidth,
        this.brickHeight,
        `rgba(255, 255, 255, ${flashAlpha * 0.8})`
      );
    }
  }

  private lightenColor(color: string, amount: number): string {
    const rgb = this.hexToRgb(color);
    return `rgb(${Math.min(255, rgb.r + amount * 255)}, ${Math.min(255, rgb.g + amount * 255)}, ${Math.min(255, rgb.b + amount * 255)})`;
  }

  private darkenColor(color: string, amount: number): string {
    const rgb = this.hexToRgb(color);
    return `rgb(${Math.max(0, rgb.r - amount * 255)}, ${Math.max(0, rgb.g - amount * 255)}, ${Math.max(0, rgb.b - amount * 255)})`;
  }

  private hexToRgb(hex: string): { r: number, g: number, b: number } {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 255, g: 255, b: 255 };
  }
}